import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Sparkles, ArrowLeft, Loader2, Copy, Users, PoundSterling,
  TrendingUp, Clock, Check, UserPlus, Share2, Award, Pencil, X
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';
import AppNav from '@/components/AppNav';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const AffiliateDashboard = () => {
  const { user, isAuthenticated, getAuthHeader, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [affiliate, setAffiliate] = useState(null);
  const [stats, setStats] = useState(null);
  const [parentCode, setParentCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [editingCode, setEditingCode] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [savingCode, setSavingCode] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchAffiliateData();
  }, [authLoading, isAuthenticated]);

  const fetchAffiliateData = async () => {
    try {
      const response = await axios.get(`${API}/api/affiliates/me`, {
        headers: getAuthHeader()
      });
      setAffiliate(response.data);
      
      // Fetch stats
      const statsResponse = await axios.get(`${API}/api/affiliates/stats`, {
        headers: getAuthHeader()
      });
      setStats(statsResponse.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching affiliate data:', error);
      }
      // User is not an affiliate yet
      setAffiliate(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setRegistering(true);
    try {
      const response = await axios.post(`${API}/api/affiliates/register`, 
        { parent_code: parentCode || null },
        { headers: getAuthHeader() }
      );
      setAffiliate(response.data);
      toast.success('You are now a Poettik Affiliate!');
      fetchAffiliateData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to register as affiliate';
      toast.error(message);
    } finally {
      setRegistering(false);
    }
  };

  const copyCode = () => {
    if (affiliate?.affiliate_code) {
      navigator.clipboard.writeText(affiliate.affiliate_code);
      setCopied(true);
      toast.success('Affiliate code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyLink = () => {
    if (affiliate?.affiliate_code) {
      const link = `${window.location.origin}/?ref=${affiliate.affiliate_code}`;
      navigator.clipboard.writeText(link);
      toast.success('Referral link copied!');
    }
  };

  const saveCustomCode = async () => {
    const clean = customCode.trim().toUpperCase();
    if (!clean) {
      toast.error('Please enter a code');
      return;
    }
    setSavingCode(true);
    try {
      const res = await axios.put(
        `${API}/api/affiliate/customise-code`,
        { code: clean },
        { headers: getAuthHeader() }
      );
      setAffiliate((prev) => ({
        ...prev,
        affiliate_code: res.data.affiliate_code,
        code_customised_at: res.data.code_customised_at,
      }));
      setEditingCode(false);
      setCustomCode('');
      toast.success(`Your affiliate code is now ${res.data.affiliate_code}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save code');
    } finally {
      setSavingCode(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <SEO title="Affiliate Dashboard" description="Track your Poettik affiliate referrals and commission earnings." noindex />
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-20 -left-40 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <AppNav />
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {!affiliate ? (
          // Registration View
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h1 className="font-display text-3xl font-bold mb-2">Become a Poettik Affiliate</h1>
              <p className="text-muted-foreground">
                Earn commissions by referring customers and other affiliates to Poettik
              </p>
            </div>

            <Card variant="glass" className="mb-8">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Commission Structure</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-sm font-bold text-primary">1</span>
                      </div>
                      <span>Direct sales (people who use your code)</span>
                    </div>
                    <span className="font-bold text-primary">30%</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                        <span className="text-sm font-bold text-accent">2</span>
                      </div>
                      <span>Sales by affiliates you recruited</span>
                    </div>
                    <span className="font-bold text-accent">7%</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center">
                        <span className="text-sm font-bold text-secondary">3</span>
                      </div>
                      <span>Sales by their recruits</span>
                    </div>
                    <span className="font-bold text-secondary">3%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card variant="glass">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="parentCode">Referral Code (optional)</Label>
                    <Input
                      id="parentCode"
                      placeholder="Enter code if you were referred by someone"
                      value={parentCode}
                      onChange={(e) => setParentCode(e.target.value.toUpperCase())}
                    />
                    <p className="text-xs text-muted-foreground">
                      If another affiliate referred you, enter their code to join their network
                    </p>
                  </div>

                  <Button 
                    variant="premium" 
                    size="lg" 
                    className="w-full"
                    onClick={handleRegister}
                    disabled={registering}
                  >
                    {registering ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Registering...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-5 h-5 mr-2" />
                        Become an Affiliate
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          // Dashboard View
          <>
            <div className="mb-8">
              <h1 className="font-display text-3xl font-bold mb-2">Affiliate Dashboard</h1>
              <p className="text-muted-foreground">
                Welcome back! Track your earnings and manage your affiliate network.
              </p>
            </div>

            {/* Eligibility Banner */}
            {stats?.eligibility && stats.eligibility.status !== 'active' && stats.eligibility.status !== 'override' && (
              <div
                className={`mb-6 rounded-lg border p-4 flex items-start gap-3 ${
                  stats.eligibility.status === 'grace'
                    ? 'border-amber-500/30 bg-amber-500/5'
                    : 'border-rose-500/30 bg-rose-500/5'
                }`}
                data-testid="affiliate-eligibility-banner"
              >
                <div className={`mt-0.5 ${stats.eligibility.status === 'grace' ? 'text-amber-400' : 'text-rose-400'}`}>
                  {stats.eligibility.status === 'grace' ? '⏳' : '🔒'}
                </div>
                <div className="flex-1">
                  {stats.eligibility.status === 'grace' ? (
                    <>
                      <p className="font-medium text-amber-300">You're in your 30-day grace window</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Reactivate your plan before{' '}
                        <span className="text-foreground font-medium">
                          {stats.eligibility.grace_until ? new Date(stats.eligibility.grace_until).toLocaleDateString() : 'soon'}
                        </span>{' '}
                        to keep earning commissions on new sales.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-rose-300">
                        Commissions on hold{stats.held_earnings > 0 ? ` — £${(stats.held_earnings).toFixed(2)} waiting` : ''}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        You need an active Poettik plan to earn (and claim) affiliate commissions.
                        {stats.held_earnings > 0 && ' Reactivate now to release the held amount above.'}
                      </p>
                      <Link
                        to="/pricing"
                        className="inline-block mt-2 text-sm text-rose-300 hover:text-rose-200 underline underline-offset-2"
                        data-testid="affiliate-reactivate-link"
                      >
                        Reactivate your plan →
                      </Link>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Affiliate Code Card */}
            <Card variant="gradient" className="mb-8">
              <CardContent className="p-6">
                {!editingCode ? (
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm text-muted-foreground">Your Affiliate Code</p>
                        {affiliate.code_customised_at && (
                          <span
                            className="text-[10px] uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded"
                            data-testid="affiliate-code-custom-badge"
                          >
                            Custom
                          </span>
                        )}
                      </div>
                      <p className="font-display text-3xl font-bold" data-testid="affiliate-code-display">
                        {affiliate.affiliate_code}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Poettik Affiliate · Earns 30% on direct sales
                      </p>
                      {stats?.recruited_by && (
                        <p className="text-xs text-muted-foreground mt-1" data-testid="affiliate-recruited-by">
                          Recruited by{' '}
                          <span className="text-foreground font-medium">{stats.recruited_by.name}</span>
                          <span className="font-mono ml-1">({stats.recruited_by.code})</span>
                        </p>
                      )}
                      {!affiliate.code_customised_at && (
                        <button
                          type="button"
                          onClick={() => { setEditingCode(true); setCustomCode(''); }}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                          data-testid="affiliate-code-personalise-btn"
                        >
                          <Pencil className="w-3 h-3" />
                          Personalise this code (one-time only)
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={copyCode} data-testid="affiliate-code-copy-btn">
                        {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                        Copy Code
                      </Button>
                      <Button variant="gradient" size="sm" onClick={copyLink} data-testid="affiliate-link-copy-btn">
                        <Share2 className="w-4 h-4 mr-2" />
                        Copy Link
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="affiliate-code-edit-panel">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Personalise your code</p>
                        <p className="text-xs text-muted-foreground">
                          Pick something memorable for your audience (e.g. SARAH26, MIKEDEALS, GLOWUP).
                          <strong className="text-foreground"> This can only be done once.</strong>
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setEditingCode(false); setCustomCode(''); }}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid="affiliate-code-cancel-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={customCode}
                        onChange={(e) => setCustomCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                        placeholder="YOURCODE"
                        maxLength={20}
                        className="font-mono tracking-wider uppercase flex-1"
                        data-testid="affiliate-code-input"
                        onKeyDown={(e) => e.key === 'Enter' && !savingCode && saveCustomCode()}
                      />
                      <Button
                        variant="gradient"
                        size="default"
                        onClick={saveCustomCode}
                        disabled={savingCode || customCode.length < 4}
                        data-testid="affiliate-code-save-btn"
                      >
                        {savingCode ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                        Save code
                      </Button>
                    </div>
                    <ul className="text-xs text-muted-foreground space-y-1 ml-1">
                      <li>· 4–20 characters, letters and numbers only</li>
                      <li>· Must be unique (someone else may have already taken it)</li>
                      <li>· No reserved words (e.g. POETTIK, ADMIN, FREE)</li>
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card variant="glass">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <PoundSterling className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Earnings</p>
                      <p className="font-display text-2xl font-bold">
                        £{(stats?.total_earnings || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card variant="glass">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Pending</p>
                      <p className="font-display text-2xl font-bold">
                        £{(stats?.pending_earnings || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {(stats?.held_earnings || 0) > 0 ? (
                <Card variant="glass" data-testid="affiliate-held-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-rose-500/20 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-rose-400" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">On Hold</p>
                        <p className="font-display text-2xl font-bold text-rose-300">
                          £{(stats?.held_earnings || 0).toFixed(2)}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">Reactivate to claim</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card variant="glass">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-success" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Direct Sales</p>
                        <p className="font-display text-2xl font-bold">
                          {stats?.direct_sales || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card variant="glass">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Sub-Affiliates</p>
                      <p className="font-display text-2xl font-bold">
                        {stats?.sub_affiliate_count || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Earnings Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <Card variant="glass">
                <CardHeader>
                  <CardTitle className="text-lg">Commission Tiers</CardTitle>
                  <CardDescription className="text-xs">
                    Every Poettik affiliate earns 30% on their own sales. Tiers below show overrides earned
                    on people in your downline.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Tier 1 — Direct sales (30%)</span>
                        <span className="font-medium">
                          £{((stats?.total_earnings || 0) - (stats?.level_2_earnings || 0) - (stats?.level_3_earnings || 0)).toFixed(2)}
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: '100%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Tier 2 — From your recruits (7%)</span>
                        <span className="font-medium">£{(stats?.level_2_earnings || 0).toFixed(2)}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-accent" style={{ width: stats?.level_2_earnings ? '50%' : '0%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Tier 3 — From their recruits (3%)</span>
                        <span className="font-medium">£{(stats?.level_3_earnings || 0).toFixed(2)}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-secondary" style={{ width: stats?.level_3_earnings ? '25%' : '0%' }} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card variant="glass">
                <CardHeader>
                  <CardTitle className="text-lg">Your Network</CardTitle>
                  <CardDescription className="text-xs">
                    People you recruited directly + how many they've recruited.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 mb-3" data-testid="affiliate-network-summary">
                    <div>
                      <p className="text-xs text-muted-foreground">Total in your downline</p>
                      <p className="font-display text-xl font-bold">
                        {(stats?.sub_affiliate_count || 0) + (stats?.indirect_sub_affiliate_count || 0)}
                      </p>
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      <p>{stats?.sub_affiliate_count || 0} direct</p>
                      <p>{stats?.indirect_sub_affiliate_count || 0} indirect</p>
                    </div>
                  </div>
                  {stats?.sub_affiliates?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.sub_affiliates.map((sub, index) => (
                        <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                              <span className="text-xs font-bold">{sub.name?.charAt(0) || '?'}</span>
                            </div>
                            <div>
                              <p className="font-medium text-sm">{sub.name}</p>
                              <p className="text-xs text-muted-foreground">{sub.code}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">{sub.total_referrals} sales</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No direct recruits yet</p>
                      <p className="text-sm">Share your code to start earning Tier 2 overrides!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Commissions */}
            <Card variant="glass">
              <CardHeader>
                <CardTitle className="text-lg">Recent Commissions</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.recent_commissions?.length > 0 ? (
                  <div className="space-y-2">
                    {stats.recent_commissions.map((commission, index) => {
                      const tierLabel =
                        commission.level === 1 ? 'Tier 1 — Direct sale (30%)'
                        : commission.level === 2 ? 'Tier 2 — From your recruit (7%)'
                        : 'Tier 3 — From their recruit (3%)';
                      return (
                        <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              commission.level === 1 ? 'bg-primary/20' :
                              commission.level === 2 ? 'bg-accent/20' : 'bg-secondary/20'
                            }`}>
                              <Award className={`w-4 h-4 ${
                                commission.level === 1 ? 'text-primary' :
                                commission.level === 2 ? 'text-accent' : 'text-secondary'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{tierLabel}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(commission.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">£{commission.amount.toFixed(2)}</p>
                            <p className={`text-xs ${commission.status === 'paid' ? 'text-success' : 'text-muted-foreground'}`}>
                              {commission.status}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <PoundSterling className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No commissions yet</p>
                    <p className="text-sm">Start sharing your code to earn!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default AffiliateDashboard;
